import * as affineTransformOperator from "@arcgis/core/geometry/operators/affineTransformOperator.js";
import Transformation from "@arcgis/core/geometry/operators/support/Transformation";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import EditorViewModel from "@arcgis/core/widgets/Editor/EditorViewModel";
import type { EditorViewModelProperties } from "@arcgis/core/widgets/Editor/EditorViewModel";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import { debounce, groupBy } from "lodash";
import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import InfoIcon from "../../../assets/info.svg?react";
import OverettIcon from "../../../assets/mountains.svg?react";
import { AppContext, OverettContext, VersionContext } from "../../../Context";
import { setHasUnsavedChanges, setLoading } from "../../../store/appSlice";
import { setIsEditing, setIsSavingLighthouse } from "../../../store/lighthouseSlice";
import {
    setIsOverettChartOpen,
    setLowerOverettAltitudeAboveTerrain,
    setObservationHeight,
    setOverettAngle,
    setUpperOverettAltitudeAboveTerrain,
} from "../../../store/overettSlice";
import { StoreState } from "../../../store/rootReducer";
import { applyEdits, computeTrueNorth, getAllRelatedFeatures, getMapView } from "../../../utils/arcgisUtils";
import { uuidv4 } from "../../../utils/helpers";
import { LighthouseData } from "../../../utils/lighthouseConstants";
import { NFSStatus, setNewStatusToFeature } from "../../../utils/nfsUtils";
import {
    hasSectorGeometry,
    hideLighthouses,
    loadLighthouse,
    saveLighthouse,
    unhideLighthouses,
} from "../../../utils/lighthouseUtils";
import {
    calculateAngle,
    calculateSensitivity,
    distanceBetweenLowerOverettAndObserver,
    distanceBetweenOverett,
    getOverettElevations,
    getOverettFeatures,
    getOverettLinjeEndPoint,
} from "../../../utils/overettUtils";
import Collapsible from "../../genericComponents/Collapsible/Collapsible";
import InputField from "../../genericComponents/InputField/InputField";
import Loader from "../../genericComponents/Loader/Loader";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import Toggle from "../../genericComponents/Toggle/Toggle";
import { drawSectors } from "../LightHouse/lighthouseDrawing";
import { addOverlayGraphics, updateOverettLinje } from "./OverettControl";
import "./OverettEditorContent.css";
import OverettObject from "./OverettObject";
import { FeatureClasses, NauticalClasses } from "../../../constants/NauticalObjects";
import { Navlystype } from "../../../constants/nfsDomains";
import type { UpdateEvent } from "@arcgis/core/widgets/Sketch/types";
import type SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";

export enum OverettCreationStep {
    SelectObjects = 0,
    DrawLine = 1,
    EditOverett = 2,
}

interface OverettEditorContentProps {
    exitOverettEditor: () => void;
    highlightOverettFeatures: () => void;
    setFeaturesBeforeEdit: Dispatch<SetStateAction<Graphic[]>>;
}

const OverettEditorContent = (props: OverettEditorContentProps) => {
    const { exitOverettEditor, highlightOverettFeatures, setFeaturesBeforeEdit } = props;

    const dispatch = useDispatch();

    const appContext = useContext(AppContext);
    const overettContext = useContext(OverettContext);
    const versionContext = useContext(VersionContext);
    const overettCreationStep = useSelector((state: StoreState) => state.overett.overettCreationStep);
    const overettObjects = overettContext?.overettObjects.value;
    const overettLinjeRef = overettContext?.overettLinjeRef.value;
    // const overettHighlightsRef = overettContext?.overettHighlightsRef;
    const editorVMUpperOverett = overettContext?.editorVMUpperOverett.value;
    const editorVMLowerOverett = overettContext?.editorVMLowerOverett.value;
    const observer = overettContext?.overettObserver.value;
    const setObserver = overettContext?.overettObserver.set;

    const isOverettChartOpen = useSelector((state: StoreState) => state.overett.isOverettChartOpen);
    const observationHeight = useSelector((state: StoreState) => state.overett.observationHeight);
    const upperAltitudeAboveTerrain = useSelector(
        (state: StoreState) => state.overett.upperOverettAltitudeAboveTerrain
    );
    const lowerAltitudeAboveTerrain = useSelector(
        (state: StoreState) => state.overett.lowerOverettAltitudeAboveTerrain
    );
    const overettAngle = useSelector((state: StoreState) => state.overett.overettAngle);
    const showRemovedFeatures = useSelector((state: StoreState) => state.app.showRemovedFeatures);
    const versionInfo = useSelector((state: StoreState) => state.version.versionInfo);

    const hasUnsavedChanges = useSelector((state: StoreState) => state.app.hasUnsavedChanges);

    const [overettUpper, setOverettUpper] = useState<Graphic | null>(null);
    const [overettLower, setOverettLower] = useState<Graphic | null>(null);
    const [overettUpperAltitude, setOverettUpperAltitude] = useState<number>(0);
    const [overettLowerAltitude, setOverettLowerAltitude] = useState<number>(0);
    const [waterWidth, setWaterWidth] = useState<number>(100);
    const [descriptionTemp, setDescriptionTemp] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [showingSensitivityInfo, setShowingSensitivityInfo] = useState(false);
    const [upperWorkingAreaLen, setUpperWorkingAreaLen] = useState<number>(0);
    const [lowerWorkingAreaLen, setLowerWorkingAreaLen] = useState<number>(0);
    const [verticalAngle, setVerticalAngle] = useState<number>(0);
    const [overettLinjeOverlay, setOverettLinjeOverlay] = useState<Graphic | null>(null);
    const [sensitivity, setSensitivity] = useState<number>(0);
    const [isEditingOverett, setIsEditingOverett] = useState<boolean>(false);
    const overettUpperLighthouseDataRef = useRef<LighthouseData | null>(null);
    const overettLowerLighthouseDataRef = useRef<LighthouseData | null>(null);

    const [canSave, setCanSave] = useState<boolean>(true);
    const canSaveRef = useRef(canSave);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const overettUpperRef = useRef(overettUpper);
    const overettLowerRef = useRef(overettLower);
    const observerRef = useRef(observer);
    const featuresWithGeometryRef = useRef<Graphic[]>([]);
    const overettUpperMoveStartRef = useRef<Graphic | null>(null);
    const overettLowerMoveStartRef = useRef<Graphic | null>(null);
    const upperAltitudeAboveTerrainRef = useRef<number>(upperAltitudeAboveTerrain);
    const lowerAltitudeAboveTerrainRef = useRef<number>(lowerAltitudeAboveTerrain);
    const overettUpperAltitudeRef = useRef<number>(overettUpperAltitude);
    const overettLowerAltitudeRef = useRef<number>(overettLowerAltitude);
    const upperWorkingAreaLenRef = useRef<number>(upperWorkingAreaLen);
    const lowerWorkingAreaLenRef = useRef<number>(lowerWorkingAreaLen);

    const versionManagementService = versionContext?.versionServiceRef.value.current;

    const isLocked = async () => {
        const versionInfoExtended = await versionManagementService?.getVersionInfoExtended(
            versionInfo!.versionIdentifier
        );
        const isLocked = versionInfoExtended?.isLocked ?? false;

        return isLocked;
    };

    useEffect(() => {
        overettUpperRef.current = overettUpper;
        if (overettUpper) {
            overettUpperRef.current!.attributes.lighthouseData = overettUpper?.attributes.lighthouseData;
        }

        if (!overettUpper || !overettLinjeOverlay) return;
        updateOverettLinje(
            [(overettUpper?.geometry as Point).x, (overettUpper?.geometry as Point).y],
            [
                (overettLinjeOverlay?.geometry as Polyline).paths[0][1][0],
                (overettLinjeOverlay?.geometry as Polyline).paths[0][1][1],
            ],
            (angle: number) => dispatch(setOverettAngle(angle))
        );
    }, [overettUpper]);

    useEffect(() => {
        overettLowerRef.current = overettLower;
    }, [overettLower]);

    useEffect(() => {
        observerRef.current = observer;
    }, [observer]);

    useEffect(() => {
        canSaveRef.current = canSave;
    }, [canSave]);

    useEffect(() => {
        upperAltitudeAboveTerrainRef.current = upperAltitudeAboveTerrain;
    }, [upperAltitudeAboveTerrain]);

    useEffect(() => {
        lowerAltitudeAboveTerrainRef.current = lowerAltitudeAboveTerrain;
    }, [lowerAltitudeAboveTerrain]);

    useEffect(() => {
        overettUpperAltitudeRef.current = overettUpperAltitude;
    }, [overettUpperAltitude]);

    useEffect(() => {
        overettLowerAltitudeRef.current = overettLowerAltitude;
    }, [overettLowerAltitude]);

    useEffect(() => {
        upperWorkingAreaLenRef.current = upperWorkingAreaLen;
    }, [upperWorkingAreaLen]);

    useEffect(() => {
        lowerWorkingAreaLenRef.current = lowerWorkingAreaLen;
    }, [lowerWorkingAreaLen]);

    const changeUpperWorkingAreaLen = () => {
        if (!overettUpper || !overettLower) {
            setUpperWorkingAreaLen(0);
            return;
        }

        const distance = distanceBetweenOverett(
            overettUpper.geometry as Point,
            upperAltitudeAboveTerrainRef.current,
            overettLower.geometry as Point,
            lowerAltitudeAboveTerrainRef.current
        );
        setUpperWorkingAreaLen(distance);
    };

    const changeLowerWorkingAreaLen = () => {
        if (!overettLower || !observerRef.current) {
            setLowerWorkingAreaLen(0);
            return;
        }

        const distance = distanceBetweenLowerOverettAndObserver(
            overettLower.geometry as Point,
            lowerAltitudeAboveTerrainRef.current,
            observerRef.current,
            observationHeight
        );
        setLowerWorkingAreaLen(distance);
    };

    const changeVerticalAngle = () => {
        const angleValue = calculateAngle(
            lowerAltitudeAboveTerrainRef.current,
            overettLowerAltitudeRef.current,
            upperAltitudeAboveTerrainRef.current,
            overettUpperAltitudeRef.current,
            observationHeight,
            upperWorkingAreaLenRef.current,
            lowerWorkingAreaLenRef.current
        );

        setVerticalAngle(angleValue);
    };

    const changeSensitivity = () => {
        if (overettLowerAltitude + lowerAltitudeAboveTerrain === overettUpperAltitude + upperAltitudeAboveTerrain) {
            setSensitivity(0);
            return;
        }
        const sensitivityValue = calculateSensitivity(
            lowerAltitudeAboveTerrainRef.current,
            overettLowerAltitudeRef.current,
            upperAltitudeAboveTerrainRef.current,
            overettUpperAltitudeRef.current,
            waterWidth,
            upperWorkingAreaLenRef.current,
            lowerWorkingAreaLenRef.current
        );
        setSensitivity(sensitivityValue);
    };

    useEffect(() => {
        changeUpperWorkingAreaLen();
    }, [overettUpper, overettLower, upperAltitudeAboveTerrain, lowerAltitudeAboveTerrain]);

    useEffect(() => {
        changeLowerWorkingAreaLen();
    }, [overettUpper, overettLower, observer]);

    const applyEditDebounced = useMemo(
        () =>
            debounce(async () => {
                if (!canSaveRef.current) return;
                setCanSave(false);
                if (!overettLinjeRef?.current) return;

                await applyEdits(overettLinjeRef.current.layer as FeatureLayer, {
                    updateFeatures: [overettLinjeRef.current],
                });
                setCanSave(true);
            }, 1000),
        [overettLinjeRef?.current]
    );

    useEffect(() => {
        if (!overettLinjeRef?.current) return;

        overettLinjeRef.current.attributes.retning_verdi = overettAngle;
        overettLinjeRef.current.attributes.beskrivelse = descriptionTemp;
        overettLinjeRef.current.attributes.godhetstall = sensitivity;

        applyEditDebounced?.();
    }, [sensitivity, overettAngle, description]);

    useEffect(() => {
        changeSensitivity();
    }, [
        lowerAltitudeAboveTerrain,
        overettLowerAltitude,
        upperAltitudeAboveTerrain,
        overettUpperAltitude,
        waterWidth,
        upperWorkingAreaLen,
        lowerWorkingAreaLen,
    ]);

    useEffect(() => {
        changeVerticalAngle();
    }, [
        lowerAltitudeAboveTerrain,
        overettLowerAltitude,
        upperAltitudeAboveTerrain,
        overettUpperAltitude,
        observationHeight,
        upperWorkingAreaLen,
        lowerWorkingAreaLen,
        overettUpper,
        overettLower,
        observer,
    ]);

    // 03.12.2025 - Was not working correctly, so commented out for now
    // const removeOverettObject = async (overettObject: Graphic | nullish) => {
    //     if (!overettObject || !overettLinjeRef?.current) return;

    //     const overettObjectId = overettObject.attributes?.objectid;
    //     if (overettObjects?.lower?.attributes?.objectid === overettObjectId) {
    //         overettContext?.overettObjects.set({ upper: overettObjects?.upper, lower: null });
    //         setOverettLower(null);
    //     } else if (overettObjects?.upper?.attributes?.objectid === overettObjectId) {
    //         overettContext?.overettObjects.set({ upper: null, lower: overettObjects?.lower });
    //         setOverettUpper(null);
    //     }

    //     overettLinjeRef.current.attributes.retning_verdi = null;
    //     overettLinjeRef.current.attributes.godhetstall = null;

    //     if (!canSaveRef.current) {
    //         // wait a little while before trying again
    //         await new Promise((resolve) => setTimeout(resolve, 300));
    //     }
    //     setCanSave(false);
    //     await applyEdits(overettLinjeRef.current.layer as __esri.FeatureLayer, {
    //         updateFeatures: [overettLinjeRef.current],
    //     });
    //     setCanSave(true);

    //     const statusField =
    //         AppConfig.Layers.find((layer) => layer.Name === overettLinjeRef.current?.layer?.id)?.StatusField ??
    //         "nfs_status";
    //     const overettStatus = overettLinjeRef.current.attributes[statusField];

    //     if (overettStatus !== NFSStatus.PROJECTED) return;

    //     if (!canSaveRef.current) {
    //         // wait a little while before trying again
    //         await new Promise((resolve) => setTimeout(resolve, 300));
    //     }
    //     setCanSave(false);
    //     await applyEdits(overettLinjeRef.current.layer as __esri.FeatureLayer, {
    //         deleteFeatures: [overettLinjeRef.current],
    //     });
    //     overettHighlightsRef?.clear();
    //     overettLinjeRef.current = null;
    //     setCanSave(true);
    // };

    const hideOverettLines = async (overettLinje: Graphic | null | undefined) => {
        if (!overettLinje) return;
        const mapView = getMapView();
        if (!mapView?.map) return;

        const overettLinjeLysLayer = mapView.map.findLayerById("Overettlinje lys") as FeatureLayer;
        const overettLinjeMerkeLayer = mapView.map.findLayerById("Overettlinje merke") as FeatureLayer;

        overettLinjeLysLayer.visible = false;
        overettLinjeMerkeLayer.visible = false;
    };

    useEffect(() => {
        if (!overettLinjeRef?.current) return;

        if (overettLinjeRef.current.attributes.beskrivelse) {
            setDescriptionTemp(overettLinjeRef.current.attributes?.beskrivelse);
            setDescription(overettLinjeRef.current.attributes?.beskrivelse);
        }
    }, []);

    useEffect(() => {
        if (overettCreationStep !== OverettCreationStep.EditOverett) return;
    }, [overettCreationStep, overettObjects?.upper, overettObjects?.lower]);

    useEffect(() => {
        setOverettObjectValues();
    }, [overettObjects?.upper, overettObjects?.lower]);

    useEffect(() => {
        if (!overettLinjeOverlay || !overettUpper) return;
        const overettLinjeEndPoint = getOverettLinjeEndPoint(overettLinjeOverlay, overettUpper.geometry as Point);
        if (!overettLinjeEndPoint) return;

        setObserver?.(overettLinjeEndPoint);
    }, [overettUpper, overettLinjeOverlay, overettObjects?.upper, overettObjects?.lower]);

    useEffect(() => {
        editGeometry();
    }, [overettUpper, overettLower]);

    useEffect(() => {
        editGeometry();
    }, [overettObjects?.upper, overettObjects?.lower]);

    useEffect(() => {
        if (!overettObjects || !overettLinjeRef?.current) return;
        highlightOverettFeatures();
    }, [overettObjects?.upper, overettObjects?.lower, overettLinjeRef]);

    useEffect(() => {
        if (!overettLinjeRef?.current) return;

        if (overettLinjeRef.current.attributes.beskrivelse) {
            setDescriptionTemp(overettLinjeRef.current.attributes?.beskrivelse);
            setDescription(overettLinjeRef.current.attributes?.beskrivelse);
        }

        const view = getMapView();
        const overettLinjeLysLayer = view?.map?.findLayerById("Overettlinje lys") as FeatureLayer;
        if (overettLinjeLysLayer) {
            overettLinjeRef.current.symbol = (overettLinjeLysLayer?.renderer as SimpleRenderer)?.symbol;
        }

        addOverlayGraphics(overettLinjeRef.current);

        setOverettLinjeOverlay(overettLinjeRef.current);
    }, [overettLinjeRef?.current]);

    const setOverettObjectValues = async () => {
        const startObject = overettLinjeRef?.current ?? overettUpperRef.current ?? overettLowerRef.current;
        if (!startObject) return;

        const objects: Graphic[] = [];
        if (overettLinjeRef?.current && overettUpperRef.current && overettLowerRef.current) {
            objects.push(overettLinjeRef.current, overettUpperRef.current, overettLowerRef.current);
        } else {
            const tempObjects = await getOverettFeatures(startObject);
            if (!tempObjects) return;
            objects.push(...tempObjects);
            setFeaturesBeforeEdit(tempObjects);
        }

        if (!objects?.at(1) || !objects?.at(2)) return;

        const results = await getOverettElevations(objects.at(1)!, objects.at(2)!);
        if (!results) return;
        const [upperOverett, lowerOverett, elevation1, elevation2] = results;

        if (upperOverett) {
            (upperOverett.geometry as Point).z = elevation1;
            setOverettUpper(upperOverett);
            setOverettUpperAltitude(elevation1);
        } else {
            setOverettUpper(null);
            setOverettUpperAltitude(0);
        }

        if (lowerOverett) {
            (lowerOverett.geometry as Point).z = elevation2;
            setOverettLower(lowerOverett);
            setOverettLowerAltitude(elevation2);
        } else {
            setOverettLower(null);
            setOverettLowerAltitude(0);
        }

        changeUpperWorkingAreaLen();
        changeLowerWorkingAreaLen();
        changeVerticalAngle();
        changeSensitivity();
    };

    const calculateOverettLinje = async () => {
        const view = getMapView();
        if (!view?.map) return;

        const overettOverlayLayer = view.map.findLayerById("overettOverlayLayer") as GraphicsLayer;
        if (!overettOverlayLayer) return;
        const startPoint = overettUpperRef.current?.geometry as Point;

        const lineGraphic = overettOverlayLayer.graphics.find((graphic) => graphic.geometry?.type === "polyline");
        if (!lineGraphic) return;

        let overettLinjeLayer = "";
        if ((overettUpperRef.current?.layer as FeatureLayer).layerId === FeatureClasses.Lys) {
            overettLinjeLayer = "Overettlinje lys";
        }

        if ((overettUpperRef.current?.layer as FeatureLayer).layerId === FeatureClasses.Fastsjømerke) {
            overettLinjeLayer = "Overettlinje merke";
        }

        const newLine = await updateOverettLinje(
            [startPoint.x, startPoint.y],
            [(lineGraphic.geometry as Polyline).paths[0][1][0], (lineGraphic.geometry as Polyline).paths[0][1][1]],
            (angle: number) => dispatch(setOverettAngle(angle))
        );

        if (overettLinjeLayer !== "" && newLine) {
            const layer = view.map.findLayerById(overettLinjeLayer) as FeatureLayer;
            if (layer) {
                newLine.layer = layer;
            }
        }

        return newLine;
    };

    const editGeometry = async () => {
        if (isEditingOverett) return;

        const view = getMapView();
        if (!view?.map || !editorVMUpperOverett || !editorVMLowerOverett) return;

        setIsLoading(true);

        if (editorVMUpperOverett.current) {
            editorVMUpperOverett.current.activeWorkflow?.cancel();
            editorVMUpperOverett.current.destroy();
            editorVMUpperOverett.current = undefined;
        }
        if (editorVMLowerOverett.current) {
            editorVMLowerOverett.current.activeWorkflow?.cancel();
            editorVMLowerOverett.current.destroy();
            editorVMLowerOverett.current = undefined;
        }

        editorVMUpperOverett.current = new EditorViewModel({
            view: view,
            sketchViewModel: new SketchViewModel({
                updateOnGraphicClick: false,
            }),
        } as EditorViewModelProperties);
        editorVMLowerOverett.current = new EditorViewModel({
            view: view,
            sketchViewModel: new SketchViewModel({
                updateOnGraphicClick: false,
            }),
        } as EditorViewModelProperties);

        if (overettUpperRef.current) {
            await editorVMUpperOverett.current.startUpdateWorkflowAtFeatureEdit(overettUpperRef.current);

            editorVMUpperOverett.current.sketchViewModel?.update(
                editorVMUpperOverett.current.sketchViewModel.updateGraphics.toArray(),
                {
                    tool: "reshape",
                }
            );

            const lighthouseEditingLayer = view.map?.findLayerById("FyrlyktEditing") as GraphicsLayer;

            const overettUpperHasSectorGeometry = await hasSectorGeometry(overettUpperRef.current);
            if (overettUpperHasSectorGeometry) {
                const lightLayer = view.map?.findLayerById("Lys") as FeatureLayer;
                await loadLighthouse(
                    overettUpperRef.current.attributes.objectid,
                    lightLayer,
                    (data: LighthouseData) => {
                        overettUpperLighthouseDataRef.current = data;
                    },
                    appContext?.selectedFeature.set,
                    (loadingStatus: boolean) => dispatch(setLoading(loadingStatus)),
                    () => {},
                    overettUpperRef.current,
                    false
                );

                const trueNorth = await computeTrueNorth(overettUpperRef.current.geometry as Point);

                drawSectors({
                    graphicsLayer: lighthouseEditingLayer,
                    data: overettUpperLighthouseDataRef.current!,
                    setIsEditing: (value) => dispatch(setIsEditing(value)),
                    sectorsToFill: [],
                    opacity: 0.5,
                    drawNumbers: false,
                    trueNorthCorrection: trueNorth,
                    overrideSectorWidth: false,
                    centerFeature: overettUpperRef.current,
                });
            }

            editorVMUpperOverett.current.sketchViewModel?.on("update", async (event) => {
                await updateOverlayObjects(event, overettUpperRef.current!, overettLowerRef.current);

                const newLine = await calculateOverettLinje();

                if (newLine && event.toolEventInfo?.type === "move-start") {
                    setCanSave(false);
                    overettUpperMoveStartRef.current = overettUpperRef.current?.clone() ?? null;
                    overettUpperMoveStartRef.current!.layer = overettUpperRef.current?.layer;
                }

                if (
                    newLine &&
                    ["move-stop", "reshape-stop", "rotate-stop", "scale-stop", "vertex-remove"].includes(
                        event.toolEventInfo?.type ?? ""
                    )
                ) {
                    dispatch(setHasUnsavedChanges(true));
                    overettLinjeRef!.current = newLine;
                    overettUpper!.geometry = event.graphics[0].geometry;
                    // Moving the object is an edit, and its sectors follow its status when saved
                    setNewStatusToFeature(overettUpper!, NFSStatus.PROJECTED_EXISTING);
                    overettUpperRef.current = overettUpper;
                    setOverettObjectValues();
                    overettContext.overettObjects.set({
                        upper: overettUpperRef.current!,
                        lower: overettLowerRef.current!,
                    });
                    if (!(await isLocked())) {
                        await moveRelatedFeatures(
                            overettUpperMoveStartRef.current,
                            overettUpperLighthouseDataRef.current,
                            event.graphics[0].geometry as Point,
                            lighthouseEditingLayer
                        );
                    }
                    overettUpperMoveStartRef.current = null;
                    setCanSave(true);
                }
            });
        }

        if (overettLowerRef.current) {
            await editorVMLowerOverett.current.startUpdateWorkflowAtFeatureEdit(overettLowerRef.current);

            editorVMLowerOverett.current.sketchViewModel?.update(
                editorVMLowerOverett.current.sketchViewModel.updateGraphics.toArray(),
                {
                    tool: "reshape",
                }
            );

            const lighthouseEditingLayer = view.map?.findLayerById("FyrlyktEditing2") as GraphicsLayer;

            const overettLowerHasSectorGeometry = await hasSectorGeometry(overettLowerRef.current);
            if (overettLowerHasSectorGeometry) {
                const lightLayer = view.map?.findLayerById("Lys") as FeatureLayer;
                await loadLighthouse(
                    overettLowerRef.current.attributes.objectid,
                    lightLayer,
                    (data: LighthouseData) => {
                        overettLowerLighthouseDataRef.current = data;
                    },
                    appContext?.selectedFeature.set,
                    (loadingStatus: boolean) => dispatch(setLoading(loadingStatus)),
                    () => {},
                    overettLowerRef.current,
                    false
                );

                const trueNorth = await computeTrueNorth(overettLowerRef.current.geometry as Point);

                drawSectors({
                    graphicsLayer: lighthouseEditingLayer,
                    data: overettLowerLighthouseDataRef.current!,
                    setIsEditing: (value) => dispatch(setIsEditing(value)),
                    sectorsToFill: [],
                    opacity: 0.5,
                    drawNumbers: false,
                    trueNorthCorrection: trueNorth,
                    overrideSectorWidth: false,
                    centerFeature: overettLowerRef.current,
                });
            }

            editorVMLowerOverett.current.sketchViewModel?.on("update", async (event) => {
                await updateOverlayObjects(event, overettLowerRef.current!, overettUpperRef.current);

                const newLine = await calculateOverettLinje();

                if (newLine && event.toolEventInfo?.type === "move-start") {
                    overettLowerMoveStartRef.current = overettLowerRef.current?.clone() ?? null;
                    overettLowerMoveStartRef.current!.layer = overettLowerRef.current?.layer;
                    overettLowerMoveStartRef.current!.attributes.lighthouseData =
                        overettLowerRef.current?.attributes.lighthouseData;
                    setCanSave(false);
                }

                if (
                    newLine &&
                    ["move-stop", "reshape-stop", "rotate-stop", "scale-stop", "vertex-remove"].includes(
                        event.toolEventInfo?.type ?? ""
                    )
                ) {
                    overettLower!.geometry = event.graphics[0].geometry;
                    // Moving the object is an edit, and its sectors follow its status when saved
                    setNewStatusToFeature(overettLower!, NFSStatus.PROJECTED_EXISTING);
                    overettLowerRef.current = overettLower;
                    setOverettObjectValues();
                    overettContext.overettObjects.set({
                        upper: overettUpperRef.current!,
                        lower: overettLowerRef.current!,
                    });
                    if (!(await isLocked())) {
                        await moveRelatedFeatures(
                            overettLowerMoveStartRef.current,
                            overettLowerLighthouseDataRef.current,
                            event.graphics[0].geometry as Point,
                            lighthouseEditingLayer
                        );
                    }
                    overettLowerMoveStartRef.current = null;
                    setCanSave(true);
                }
            });
        }

        await hideLighthouses([overettUpperRef.current, overettLowerRef.current], showRemovedFeatures);

        setIsEditingOverett(Boolean(overettUpperRef.current) && Boolean(overettLowerRef.current));
        hideOverettLines(overettLinjeRef?.current);
        setIsLoading(false);
    };

    const moveRelatedFeatures = async (
        feature: Graphic | null | undefined,
        lighthouseData: LighthouseData | null,
        newPosition: Point,
        lighthouseEditingLayer: GraphicsLayer
    ) => {
        if (!feature) return;
        const view = getMapView();
        if (!view?.map) return;

        const featuresToMove: Graphic[] = [];

        if (lighthouseData) {
            lighthouseData.center = newPosition;

            lighthouseEditingLayer.visible = true;
            const trueNorth = await computeTrueNorth(feature.geometry as Point);
            drawSectors({
                graphicsLayer: lighthouseEditingLayer,
                data: lighthouseData,
                setIsEditing: (value) => dispatch(setIsEditing(value)),
                sectorsToFill: [],
                opacity: 0.5,
                drawNumbers: false,
                trueNorthCorrection: trueNorth,
                overrideSectorWidth: false,
                centerFeature: feature,
            });
        }

        const relatedFeatures = await getAllRelatedFeatures(feature);
        const relationsWithGeometry = relatedFeatures.filter(
            (relation) => relation.features.length > 0 && relation.features[0].geometry
        );
        for (const relation of relationsWithGeometry) {
            const layer = view.map?.layers.find(
                (l) => l.type === "feature" && (l as FeatureLayer).layerId === relation.relationship.relatedTableId
            ) as FeatureLayer;
            for (const element of relation.features) {
                const features = await layer.queryFeatures({
                    where: `objectid = ${element.attributes.objectid}`,
                    outFields: ["*"],
                    returnGeometry: true,
                });
                if (
                    ["Overettlinje lys", "Overettlinje merke"].includes(features.features[0].layer?.id.toString() ?? "")
                )
                    continue;
                const graphic = features.features[0].clone();
                graphic.layer = layer;

                const graphicClone = graphic.clone();
                graphicClone.layer = layer;
                setFeaturesBeforeEdit((prev) => [...prev, graphicClone]);
                featuresWithGeometryRef.current.push(graphic);

                const geometry = feature.geometry as Point;

                const dx = newPosition.x - (geometry.x ?? 0);
                const dy = newPosition.y - (geometry.y ?? 0);

                const transform = new Transformation();
                transform.shift(dx, dy);
                const newGeometry = affineTransformOperator.execute(geometry, transform);
                if (graphic.geometry) {
                    graphic.geometry = newGeometry;
                    featuresToMove.push(graphic);
                }
            }
        }

        const moveGroups = groupBy(featuresToMove, (feat) => feat.layer?.id);
        for (const [layerId, features] of Object.entries(moveGroups)) {
            const layer = view.map?.findLayerById(layerId) as FeatureLayer;
            if (!layer.hasZ) {
                for (const feature of features) {
                    if (feature.geometry?.type === "point") feature.geometry.z = undefined;
                }
            }
            if (!canSaveRef.current) {
                // wait a little while before trying again
                await new Promise((resolve) => setTimeout(resolve, 300));
            }
            await applyEdits(layer, {
                updateFeatures: features,
            });
        }
    };

    const updateOverlayObjects = async (event: UpdateEvent, thisOverett: Graphic, otherOverett?: Graphic | null) => {
        if (
            ["move-stop", "reshape-stop", "rotate-stop", "scale-stop", "vertex-remove"].includes(
                event.toolEventInfo?.type ?? ""
            )
        ) {
            const updateObject = event.graphics[0].clone();
            if (!updateObject) return;
            updateObject.geometry = thisOverett.geometry?.clone();
            updateObject.attributes = { ...thisOverett.attributes };
            updateObject.layer = thisOverett.layer as FeatureLayer;
            updateObject.attributes["objectid"] = event.graphics[0].attributes["objectid"];
            updateObject.attributes["globalid"] = event.graphics[0].attributes["globalid"];
            const overettObjects = otherOverett
                ? { upper: updateObject, lower: otherOverett }
                : { upper: updateObject, lower: null };
            overettContext?.overettObjects.set(overettObjects);
            highlightOverettFeatures();
        }
    };

    const saveLighthouseFeatures = async (features: Graphic[]) => {
        for (const feature of features) {
            if (
                feature.attributes.objectid === overettUpperRef.current?.attributes.objectid &&
                overettUpperLighthouseDataRef.current
            ) {
                const trueNorth = await computeTrueNorth(feature.geometry as Point);
                await saveLighthouse(
                    overettUpperLighthouseDataRef.current,
                    appContext?.selectedFeature.set,
                    feature,
                    trueNorth,
                    (savingLighthouseStatus: boolean) => dispatch(setIsSavingLighthouse(savingLighthouseStatus)),
                    (loadingStatus: boolean) => dispatch(setLoading(loadingStatus))
                );
            }

            if (
                feature.attributes.objectid === overettLowerRef.current?.attributes.objectid &&
                overettLowerLighthouseDataRef.current
            ) {
                const lighthouseEditingLayer = getMapView()?.map?.findLayerById("FyrlyktEditing2") as GraphicsLayer;
                const trueNorth = await computeTrueNorth(feature.geometry as Point);
                await saveLighthouse(
                    overettLowerLighthouseDataRef.current,
                    appContext?.selectedFeature.set,
                    feature,
                    trueNorth,
                    (savingLighthouseStatus: boolean) => dispatch(setIsSavingLighthouse(savingLighthouseStatus)),
                    (loadingStatus: boolean) => dispatch(setLoading(loadingStatus)),
                    lighthouseEditingLayer
                );
            }
        }
    };

    const saveOverettPlacement = async () => {
        if (!overettObjects || !overettLinjeRef?.current) return;
        const overettLayer = overettObjects.upper?.layer as FeatureLayer;
        if (!overettLayer) return;

        const mapView = getMapView();
        if (!mapView?.map) return;

        const overettLinjeLysLayer = mapView.map.findLayerById("Overettlinje lys") as FeatureLayer;
        const overettLinjeMerkeLayer = mapView.map.findLayerById("Overettlinje merke") as FeatureLayer;

        if (!overettLinjeLysLayer || !overettLinjeMerkeLayer) return;

        dispatch(setLoading(true));
        const overettLinjeLayer = ["Lys", "Overettlinje lys"].includes(overettLayer.id)
            ? overettLinjeLysLayer
            : overettLinjeMerkeLayer;

        for (const overettObject of [overettObjects.upper, overettObjects.lower]) {
            if (overettObject) {
                (overettObject.geometry as Point).z = undefined;
            }
        }
        overettLinjeRef.current.layer = overettLinjeLayer;
        overettLinjeRef.current.attributes.klasse = 11;
        if (!canSaveRef.current) {
            // wait a little while before trying again
            await new Promise((resolve) => setTimeout(resolve, 300));
        }

        if (
            overettObjects.upper?.attributes.klasse === NauticalClasses.LA_Lanterne ||
            overettObjects.upper?.attributes.klasse === NauticalClasses.HIB_HurtigbaatmerkeMedIndirekteBelysning
        ) {
            overettObjects.upper.attributes.lys_type = Navlystype.Overettlys;
        }
        if (
            overettObjects.lower?.attributes.klasse === NauticalClasses.LA_Lanterne ||
            overettObjects.lower?.attributes.klasse === NauticalClasses.HIB_HurtigbaatmerkeMedIndirekteBelysning
        ) {
            overettObjects.lower.attributes.lys_type = Navlystype.Overettlys;
        }

        await applyEdits(overettLayer, {
            updateFeatures: [overettObjects.upper, overettObjects.lower].filter(Boolean) as Graphic[],
        });

        (overettLinjeRef.current.geometry as Polyline).setPoint(0, 0, overettObjects.upper?.geometry as Point);

        await applyEdits(overettLinjeLayer, {
            updateFeatures: [overettLinjeRef.current],
        });

        dispatch(setLoading(true));
        editorVMUpperOverett?.current?.destroy();
        editorVMLowerOverett?.current?.destroy();
        editorVMUpperOverett!.current = undefined;
        editorVMUpperOverett!.current = undefined;

        setFeaturesBeforeEdit([]);

        saveLighthouseFeatures([overettObjects.upper, overettObjects.lower].filter(Boolean) as Graphic[]);
        unhideLighthouses(showRemovedFeatures);

        exitOverettEditor();

        dispatch(setLoading(false));
        dispatch(setHasUnsavedChanges(false));
    };

    return (
        <>
            <div className="feature-viewer-content" id="overett-editor-content">
                <h2>Overett</h2>
                <span className="overett-info-label">Øvre overett:</span>
                <OverettObject object={overettUpper} />
                <span className="overett-info-label">Nedre overett:</span>
                <OverettObject object={overettLower} />
                <div className="overett-infobox">
                    <div className="overett-show-height-profile">
                        <div className="flex-row">
                            <div>
                                <OverettIcon />
                                <span>Vis høydeprofil</span>
                            </div>
                        </div>
                        <Toggle
                            checked={isOverettChartOpen}
                            onChange={(value) => dispatch(setIsOverettChartOpen(value))}
                            small
                        />
                    </div>
                    <span className="overett-info-label">Overettens høyde over høyvann</span>
                    <div className="flex-row small-gap">
                        <InputField
                            inputFieldId={uuidv4()}
                            label="Øvre"
                            onChange={() => {}}
                            type="text"
                            value={overettUpper ? overettUpperAltitude.toFixed(2) : "—"}
                            readonly
                            valueSuffix="m"
                        />
                        <InputField
                            inputFieldId={uuidv4()}
                            label="Nedre"
                            onChange={() => {}}
                            type="text"
                            value={overettLower ? overettLowerAltitude.toFixed(2) : "—"}
                            readonly
                            valueSuffix="m"
                        />
                    </div>
                    <span className="overett-info-label">Overettens høyde over terreng</span>
                    <div className="flex-row small-gap">
                        <InputField
                            inputFieldId={uuidv4()}
                            label="Øvre"
                            onChange={(value) => {
                                if (!hasUnsavedChanges) {
                                    dispatch(setHasUnsavedChanges(true));
                                }
                                dispatch(setUpperOverettAltitudeAboveTerrain(Number.parseFloat(value)));
                            }}
                            type="number"
                            value={upperAltitudeAboveTerrainRef.current}
                            min={0}
                            valueSuffix="m"
                        />
                        <InputField
                            inputFieldId={uuidv4()}
                            label="Nedre"
                            onChange={(value) => {
                                if (!hasUnsavedChanges) {
                                    dispatch(setHasUnsavedChanges(true));
                                }
                                dispatch(setLowerOverettAltitudeAboveTerrain(Number.parseFloat(value)));
                            }}
                            type="number"
                            value={lowerAltitudeAboveTerrainRef.current}
                            min={0}
                            valueSuffix="m"
                        />
                    </div>
                    <Collapsible title="Overettdetaljer" isOpen={isDetailsOpen} setIsOpen={setIsDetailsOpen}>
                        <div className="flex-row small-gap">
                            <InputField
                                inputFieldId={uuidv4()}
                                label="Retning for overetten"
                                onChange={() => {}}
                                type="text"
                                value={overettUpper && overettLower ? overettAngle.toFixed(2) : "—"}
                                readonly
                                valueSuffix="degrees"
                            />
                            <InputField
                                inputFieldId={uuidv4()}
                                label="Observasjonshøyde"
                                onChange={(value) => {
                                    if (!hasUnsavedChanges) {
                                        dispatch(setHasUnsavedChanges(true));
                                    }
                                    dispatch(setObservationHeight(Number.parseFloat(value)));
                                }}
                                type="number"
                                value={observationHeight}
                                min={0}
                                valueSuffix="m"
                            />
                        </div>
                        <InputField
                            inputFieldId={uuidv4()}
                            label="Virkeområdets ytterste farvannsbredde"
                            onChange={(value) => {
                                if (!hasUnsavedChanges) {
                                    dispatch(setHasUnsavedChanges(true));
                                }
                                setWaterWidth(Number.parseFloat(value));
                            }}
                            type="number"
                            value={waterWidth}
                            min={0}
                            valueSuffix="m"
                        />
                        <div className="overett-info-div">
                            <span className="overett-info-label">
                                Vertikal vinkel mellom overettene målt fra ytre virkeområde skal være større enn (&gt;)
                                4,5°:
                            </span>
                            <span>{overettUpper && overettLower ? `${verticalAngle.toFixed(2)}°` : "—"}</span>
                        </div>
                        <div className="overett-info-div">
                            <span className="overett-info-label">Avstanden mellom øvre og nedre overett:</span>
                            <span>{overettUpper && overettLower ? `${upperWorkingAreaLen.toFixed(2)} m` : "—"}</span>
                        </div>
                        <div className="overett-info-div">
                            <span className="overett-info-label">
                                Avstanden fra overettens ytterste virkeområde til nedre lys:
                            </span>
                            <span>{overettUpper && overettLower ? `${lowerWorkingAreaLen.toFixed(2)} m` : "—"}</span>
                        </div>
                        <div className="overett-info-div">
                            <span className="overett-info-label">Overettens godhetstall:</span>
                            <div className="flex-row space-between">
                                <span>{overettUpper && overettLower ? sensitivity.toFixed(2) : "—"}</span>
                                <button
                                    className="empty-button"
                                    onClick={() => setShowingSensitivityInfo(!showingSensitivityInfo)}
                                >
                                    <InfoIcon />
                                </button>
                            </div>
                            {showingSensitivityInfo && (
                                <div className="overett-sensitivity-info">
                                    <div>
                                        <span>Dårlig</span>
                                        <span>0 - 1.0</span>
                                    </div>
                                    <div>
                                        <span>Akseptabel</span>
                                        <span>1.0 - 1.5</span>
                                    </div>
                                    <div>
                                        <span>God</span>
                                        <span>1.5 - 2.5</span>
                                    </div>
                                    <div>
                                        <span>Meget god</span>
                                        <span>2.5 - 3.5</span>
                                    </div>
                                    <div>
                                        <span>For følsom under enkelte forhold</span>
                                        <span>3.5 - 4.5</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <InputField
                            inputFieldId={uuidv4()}
                            label="Beskrivelse"
                            onChange={(value) => {
                                if (!hasUnsavedChanges) {
                                    dispatch(setHasUnsavedChanges(true));
                                }
                                setDescriptionTemp(value);
                            }}
                            onBlur={(value) => {
                                setCanSave(false);
                                setDescriptionTemp(value);
                                setDescription(value);
                            }}
                            type="text"
                            value={descriptionTemp}
                        />
                    </Collapsible>
                </div>
            </div>
            <div className="overett-save-button">
                <NfsButton onClick={saveOverettPlacement} disabled={!canSave || !overettUpper || !overettLower}>
                    Lagre
                </NfsButton>
            </div>
            {isLoading && <Loader overlay="fullscreen" />}
        </>
    );
};

export default OverettEditorContent;
